// frontend/src/landing/components/KPI.tsx
import React, { useEffect, useMemo, useState } from "react";
import { usePrefersReducedMotion } from "./usePrefersReducedMotion";

type Props = {
  label: React.ReactNode;
  ariaLabel?: string;
  value: number;
  suffix?: string;
  decimals?: number;
  animate?: boolean;
  active?: boolean;
  note?: string;

  min?: number;          // default 0
  max?: number;          // default 100
  showBar?: boolean;     // default true
};

function clamp(x: number, a: number, b: number) {
  return Math.max(a, Math.min(b, x));
}

// Map 0..100 -> hue (red..green)
function hueForPercent(pct: number) {
  const p = clamp(pct, 0, 100) / 100;
  const hueRed = 25;
  const hueGreen = 145;
  return hueRed + (hueGreen - hueRed) * p;
}

export default function KPI({
  label,
  ariaLabel,
  value,
  suffix = "",
  decimals = 2,
  animate = true,
  active = true,
  note,
  min = 0,
  max = 100,
  showBar = true,
}: Props) {
  const reduced = usePrefersReducedMotion();
  const target = useMemo(() => value, [value]);

  const [display, setDisplay] = useState<number>(() => (animate ? min : target));

  useEffect(() => {
    if (!active) return;

    if (!animate || reduced) {
      setDisplay(target);
      return;
    }

    let raf = 0;
    const start = performance.now();
    const duration = 1500; // langsamer

    const from = min;
    const to = target;

    const tick = (t: number) => {
      const p = clamp((t - start) / duration, 0, 1);
      // easeInOutCubic (ruhiger als nur easeOut)
      const eased =
        p < 0.5 ? 4 * p * p * p : 1 - Math.pow(-2 * p + 2, 3) / 2;

      setDisplay(from + (to - from) * eased);

      if (p < 1) raf = requestAnimationFrame(tick);
    };

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [active, animate, reduced, target, min]);

  const nf = useMemo(
    () =>
      new Intl.NumberFormat("de-DE", {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      }),
    [decimals]
  );

  const safeMax = max === min ? min + 1 : max;

  const pct = useMemo(() => {
    const p = ((display - min) / (safeMax - min)) * 100;
    return clamp(p, 0, 100);
  }, [display, min, safeMax]);

  const hue = useMemo(() => hueForPercent(pct), [pct]);

  // Einfarbiger Fill passend zum Wert
  const fillColor = useMemo(() => {
    // leicht „app-like“: etwas kräftig, aber nicht grell
    // (im Dark Mode wirkt OKLCH i.d.R. gut)
    return `oklch(0.68 0.16 ${hue.toFixed(1)})`;
  }, [hue]);

  return (
    <div className="zg-card zg-kpi">
      <div className="zg-card-meta">{label}</div>

      <div className="zg-kpi-value">
        {nf.format(display)}
        {suffix}
      </div>

      {showBar ? (
        <div
          className="zg-kpi-bar"
          role="progressbar"
          aria-label={ariaLabel ?? (typeof label === "string" ? label : undefined)}
          aria-valuemin={min}
          aria-valuemax={safeMax}
          aria-valuenow={clamp(target, min, safeMax)}
        >
          <div
            className="zg-kpi-bar-fill"
            style={{ width: `${pct}%`, background: fillColor }}
          />
        </div>
      ) : null}

      {note ? <div className="zg-card-meta zg-kpi-note">{note}</div> : null}
    </div>
  );
}